// src/lib/currency.ts
// Валюта заявки. Все фиатные суммы заявки и её инвойсов выражены в ней,
// курс (exchangeRate / platformRate) — это «валюта за 1 USDT».

export type CurrencyCode = string; // ISO 4217, напр. "RUB", "KZT"

export const DEFAULT_CURRENCY: CurrencyCode = "RUB";

/** Символ для UI. Для незнакомой валюты возвращаем сам код — лучше, чем пустота. */
const SYMBOLS: Record<string, string> = {
  RUB: "₽",
  KZT: "₸",
  UZS: "so'm",
  KGS: "с",
  TJS: "SM",
  AMD: "֏",
  AZN: "₼",
  GEL: "₾",
  MDL: "L",
  UAH: "₴",
  TMT: "m",
  USD: "$",
  EUR: "€",
};

export const normalizeCurrency = (code: unknown): CurrencyCode => {
  const s = String(code ?? "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(s) ? s : DEFAULT_CURRENCY;
};

/** Символ валюты: currencySymbol("KZT") -> "₸" */
export const currencySymbol = (code: unknown): string => {
  const c = normalizeCurrency(code);
  return SYMBOLS[c] ?? c;
};

/** Подпись курса: rateLabel("KZT") -> "₸/USDT" */
export const rateLabel = (code: unknown): string => `${currencySymbol(code)}/USDT`;

/** Сколько знаков после запятой показывать. Тенге/сум традиционно без дробной части. */
export const fractionDigits = (code: unknown): number =>
  ["KZT", "UZS"].includes(normalizeCurrency(code)) ? 0 : 2;

/* ---------- поток приёма ---------- */

/** Поток приёма заявки. Значения совпадают с перечислением на бэке. */
export const CHANNEL_MAIN = 1;
export const CHANNEL_MATCH = 2;

export type ChannelCode = typeof CHANNEL_MAIN | typeof CHANNEL_MATCH;

export const CHANNEL_OPTIONS = [
  { value: CHANNEL_MAIN, label: "Обычный вход" },
  { value: CHANNEL_MATCH, label: "Матчинг" },
];

/** Незаданный канал читаем как обычный вход — так же, как это делает бэк. */
export const normalizeChannel = (v: unknown): ChannelCode =>
  Number(v) === CHANNEL_MATCH ? CHANNEL_MATCH : CHANNEL_MAIN;

export const channelLabel = (v: unknown): string =>
  normalizeChannel(v) === CHANNEL_MATCH ? "Матчинг" : "Обычный вход";

/* ---------- надбавка к курсу ---------- */

export interface RateMarkup {
  percent: number;
  matchOnly: boolean;
  isEnabled: boolean;
}

/** Применяется ли надбавка к заявке в этом потоке. */
export const markupApplies = (markup: RateMarkup | null, channel: unknown): boolean =>
  !!markup &&
  markup.isEnabled &&
  markup.percent > 0 &&
  (!markup.matchOnly || normalizeChannel(channel) === CHANNEL_MATCH);

/**
 * Курс с надбавкой. Оператор вводит курс сделки, система прибавляет процент —
 * раньше это считали в уме. Округляем до четырёх знаков, как и остальные курсы.
 */
export const applyMarkup = (
  rate: number | null | undefined,
  markup: RateMarkup | null,
  channel: unknown,
): number | null => {
  const base = Number(rate);
  if (!isFinite(base) || base <= 0) return null;
  if (!markupApplies(markup, channel)) return base;
  return +(base * (1 + markup!.percent / 100)).toFixed(4);
};
